import React from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SUBSCRIPTION_TIERS } from './UserSubscriptionManager';
import { useLanguage } from '../contexts/LanguageContext';
import LockIcon from '@mui/icons-material/Lock';

interface ProtectedContentProps {
  children: React.ReactNode;
  requiredTier: string;
  title?: string;
  description?: string;
}

/**
 * Component to protect content based on user subscription tier
 * @param children - Content to display if user has access
 * @param requiredTier - Minimum subscription tier required (from SUBSCRIPTION_TIERS)
 * @param title - Title for the locked content message
 * @param description - Description for the locked content message
 */
const ProtectedContent: React.FC<ProtectedContentProps> = ({
  children,
  requiredTier,
  title,
  description
}) => {
  const { currentUser, hasAccessToTier, userSubscription } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Default messages based on language
  const defaultTitle = language === 'nl' 
    ? 'Premium Inhoud' 
    : 'Premium Content';
  
  const defaultDescription = language === 'nl'
    ? `Deze inhoud is alleen beschikbaar voor ${requiredTier} abonnees of hoger.`
    : `This content is only available to ${requiredTier} subscribers or higher.`;

  const getTierName = (tier: string): string => {
    switch(tier.toLowerCase()) {
      case SUBSCRIPTION_TIERS.ESSENTIAL: return 'Essential';
      case SUBSCRIPTION_TIERS.GOLD: return 'Gold';
      case SUBSCRIPTION_TIERS.ELITE: return 'Elite';
      case SUBSCRIPTION_TIERS.PLATINUM: return 'Platinum';
      default: return tier;
    }
  };

  // If user is not logged in
  if (!currentUser) {
    return (
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center', my: 3 }}>
        <LockIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {language === 'nl' ? 'Log in om toegang te krijgen' : 'Log in to access'}
        </Typography>
        <Typography variant="body1" paragraph>
          {language === 'nl' 
            ? 'Je moet ingelogd zijn om deze inhoud te bekijken.' 
            : 'You need to be logged in to view this content.'}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/login')}
        >
          {language === 'nl' ? 'Log in' : 'Log in'}
        </Button>
      </Paper>
    );
  }

  // If user has an active subscription and meets the tier requirement
  if (hasAccessToTier(requiredTier)) {
    return <>{children}</>;
  }

  // If user's subscription is inactive or doesn't meet the tier requirement
  return (
    <Paper elevation={3} sx={{ p: 4, textAlign: 'center', my: 3 }}>
      <LockIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        {title || defaultTitle}
      </Typography>
      <Typography variant="body1" paragraph>
        {description || defaultDescription}
      </Typography>
      
      {userSubscription.isActive ? (
        // User has active subscription but needs to upgrade
        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
          {language === 'nl'
            ? `Je huidige abonnement is ${getTierName(userSubscription.tier)}. Upgrade naar ${getTierName(requiredTier)} of hoger om toegang te krijgen.`
            : `Your current subscription is ${getTierName(userSubscription.tier)}. Upgrade to ${getTierName(requiredTier)} or higher to access.`}
        </Alert>
      ) : (
        // User has no active subscription
        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
          {language === 'nl'
            ? 'Je hebt momenteel geen actief abonnement. Activeer een abonnement om toegang te krijgen.'
            : 'You currently don\'t have an active subscription. Activate a subscription to gain access.'}
        </Alert>
      )}
      
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => navigate('/diensten?show=subscriptions')}
      >
        {language === 'nl' ? 'Bekijk Abonnementen' : 'View Subscriptions'}
      </Button>
    </Paper>
  );
};

export default ProtectedContent; 